import { VideoEditorLogger } from '../../utility/VideoEditorLogger';
import { AddMediaElementCommand } from './AddMediaElementCommand';

import type { Element } from '../models/Element';
import type { VideoEditor } from '../VideoEditor';
import type { TimelineMode } from './TimelineMode';

/**
 * Command for adding shape elements to the timeline with specialized shape handling.
 * Extends AddMediaElementCommand to inherit collision detection and track management,
 * while adding shape-specific initialization and validation.
 */
export class AddShapeElementCommand extends AddMediaElementCommand {
  constructor(
    editor: VideoEditor,
    element: Element,
    targetTrackId?: string,
    mode?: TimelineMode,
  ) {
    super(editor, element, targetTrackId, mode ?? editor.getTimelineMode());
  }

  execute(): boolean {
    VideoEditorLogger.logCommand(this, 'start', {
      elementId: this.element.id,
      elementType: this.element.type,
      targetTrackId: this.targetTrackId,
      mode: this.mode,
    });

    // Validate shape element properties
    if (!this.validateShapeElement()) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'Shape element validation failed',
      });
      return false;
    }

    // Call parent execute which handles timeline placement and collision detection
    const success = super.execute();

    if (success) {
      VideoEditorLogger.logCommand(this, 'complete', {
        elementId: this.element.id,
        finalTrackId: this.finalTrackId,
      });
    }

    return success;
  }

  getDescription(): string {
    const trackInfo = this.trackCreated
      ? `new shape track`
      : `track ${this.finalTrackId || this.targetTrackId || 'auto'}`;
    return `Add shape element to ${trackInfo} (${this.mode} mode)`;
  }

  /**
   * Validates that the shape element has required properties
   */
  private validateShapeElement(): boolean {
    if (this.element.type !== 'shape') {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'Element is not a shape type',
        actualType: this.element.type,
      });
      return false;
    }

    // Ensure element has a valid duration
    if (this.element.duration === null || this.element.durationSeconds <= 0) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'Shape element must have a valid duration',
        duration: this.element.duration,
      });
      return false;
    }

    // Ensure element has required shape properties
    const requiredProps = ['path', 'fill_color'];
    for (const prop of requiredProps) {
      if (
        !(prop in this.element) ||
        (this.element as any)[prop] === undefined
      ) {
        VideoEditorLogger.logCommand(this, 'error', {
          error: `Missing required shape property: ${prop}`,
        });
        return false;
      }
    }

    return true;
  }
}
